/**
 * richText — shared plumbing for Avium's rich-text surfaces.
 *
 * Storage contract (locked with the user 2026-06-11): descriptions are stored
 * as MARKDOWN — portable to Jira/the AI layer, and the same dialect the
 * Doc → Stories template already writes (## sections, - [ ] checklists).
 * Structure over decoration: heading levels (H2/H3), not arbitrary font sizes.
 *
 * Images: uploaded through Avium Files (R2) and stored in the markdown as a
 * STABLE `avium:<fileId>` src — R2 signed URLs expire hourly, so the real URL
 * is resolved (and cached) at display time, never persisted. Security note:
 * ticket descriptions are never pushed to Jira/Azure (write-back sends
 * summary/status/fields only), so embedded images cannot leak outside Avium.
 */

import { filesApi } from './api';

export const AVIUM_FILE_SCHEME = 'avium:';

export const isAviumFileSrc = (src: string): boolean => src.startsWith(AVIUM_FILE_SCHEME);

/** Build the stable markdown src for an uploaded file. */
export const aviumFileSrc = (fileId: string): string => `${AVIUM_FILE_SCHEME}${fileId}`;

// Signed URLs live 3600s server-side; re-resolve comfortably before expiry.
const RESOLVE_TTL_MS = 45 * 60 * 1000;
const cache = new Map<string, { url: Promise<string>; at: number }>();

/** Resolve a (possibly `avium:`) image src to a displayable URL. Cached. */
export function resolveFileSrc(src: string): Promise<string> {
  if (!isAviumFileSrc(src)) return Promise.resolve(src);
  const id = src.slice(AVIUM_FILE_SCHEME.length);
  const hit = cache.get(id);
  if (hit && Date.now() - hit.at < RESOLVE_TTL_MS) return hit.url;
  const url = filesApi.fileUrl(id, 'preview')
    .then(r => (r.data?.url as string) ?? '')
    .catch(() => { cache.delete(id); return ''; });
  cache.set(id, { url, at: Date.now() });
  return url;
}

/** react-markdown strips non-http(s) URLs by default — let `avium:` through. */
export function aviumUrlTransform(url: string): string {
  if (isAviumFileSrc(url)) return url;
  // Mirror react-markdown's default: allow http(s), mailto, tel, and relative.
  if (/^(https?:|mailto:|tel:|\/|#|\.)/i.test(url)) return url;
  return '';
}
